import React, { useEffect, useRef, useState } from 'react';

const CHARS_PER_TICK = 2;
const TICK_MS = 20;

interface TypewriterTextProps {
    text: string;
    /** `live` reveals at a bounded rate, for text that arrived after mount; `instant` renders it whole. */
    speed: 'live' | 'instant';
    children: (visible: string) => React.ReactNode;
}

/**
 * Reveals `text` progressively in `live` mode, or all at once in `instant` mode or under
 * `prefers-reduced-motion: reduce`. In `live` mode, growth by a suffix continues the reveal; any
 * other change restarts it from empty.
 */
export const TypewriterText: React.FC<TypewriterTextProps> = ({ text, speed, children }) => {
    const reducedMotion = !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const effectiveSpeed = reducedMotion ? 'instant' : speed;

    const [visibleLength, setVisibleLength] = useState(() => (effectiveSpeed === 'instant' ? text.length : 0));
    const previousTextRef = useRef(text);

    useEffect(() => {
        const previous = previousTextRef.current;
        previousTextRef.current = text;

        const isGrowth = text.startsWith(previous);
        if (effectiveSpeed === 'instant') {
            setVisibleLength(text.length);
            return;
        }
        if (!isGrowth) setVisibleLength(0);
    }, [text, effectiveSpeed]);

    useEffect(() => {
        if (effectiveSpeed !== 'live') return;

        // Keyed on `text`, not `visibleLength`, so arriving chunks keep one steady tick rate.
        const id = setInterval(() => {
            setVisibleLength(len => {
                if (len >= text.length) {
                    clearInterval(id);
                    return len;
                }
                return Math.min(len + CHARS_PER_TICK, text.length);
            });
        }, TICK_MS);

        return () => clearInterval(id);
    }, [effectiveSpeed, text]);

    const visible = effectiveSpeed === 'instant' ? text : text.slice(0, visibleLength);
    return <>{children(visible)}</>;
};
